import { z } from 'zod'

import { moneySchema } from '@/schemas/common'

/**
 * The `/companies` screen: one row per NEPSE-listed symbol, paged by cursor.
 *
 * Field names are snake_case — `point_change`, `percent_change` — because this
 * is the shape mm-be's v5 `CompanyListItem` used before the companies endpoint
 * was dropped from the OpenAPI surface (see `companyService.ts`), and
 * `CompanyTable.tsx` already reads those exact keys. Every other stub schema
 * in this directory is camelCase by choice; this one is snake_case by
 * contract.
 *
 * The four trading fields are nullable: a scrip that has not traded today (a
 * halt, a fresh listing before its first tick) reports no LTP, no change and
 * no volume rather than a stale or fabricated one.
 */
export const companyListRowSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  /** Kebab sector id (`sectorSlug` of the API's sector key) — the same id the
   *  Market tables filter on, so a sector chip means the same thing everywhere.
   *  Known only on a list filtered by sector: mm-be names a sector's members
   *  one sector at a time and publishes no sector per company, so an
   *  unfiltered row is `null` rather than a guess. */
  sector: z.string().min(1).nullable(),
  ltp: moneySchema.nullable(),
  point_change: moneySchema.nullable(),
  percent_change: moneySchema.nullable(),
  volume: z.number().int().nonnegative().nullable(),
})

/**
 * One page of the list, shaped like the other cursor feeds (`IpoListPage`,
 * `HappeningPage`): rows plus an opaque cursor for the next page, so the swap
 * to a real `getPage()` call is a body change in `companyService.ts` and
 * nothing here.
 */
export const companyListPageSchema = z.object({
  rows: z.array(companyListRowSchema),
  nextCursor: z.string().optional(),
  total: z.number().int().nonnegative(),
})

export type CompanyListRow = z.infer<typeof companyListRowSchema>
export type CompanyListPage = z.infer<typeof companyListPageSchema>
